/**
 * Provides application session and onboarding-modal state to the component tree.
 * It also keeps Mixpanel's user email and clinic ID synchronized with session
 * changes so tracked events automatically receive the active session context.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  setMixpanelSessionClinicId,
  setMixpanelSessionEmail,
} from "../utils/mixpanel";
import {
  TOnboardingModalState,
  TSession,
  TSessionContext,
} from "../utils/types";
import SessionContext from "./session";

type SessionProviderProps = {
  children: React.ReactNode;
};

export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
}) => {
  const [session, setSession] = useState<TSession | null>(null);
  const [onboardingModal, setOnboardingModal] = useState<TOnboardingModalState>(
    {
      visible: false,
      type: "OnboardingWelcome",
    },
  );

  const setSessionWithMixpanel = useCallback<
    React.Dispatch<React.SetStateAction<TSession | null>>
  >((nextSessionOrUpdater) => {
    if (typeof nextSessionOrUpdater === "function") {
      setSession((currentSession) => {
        const nextSession = nextSessionOrUpdater(currentSession);
        if (nextSession) {
          setMixpanelSessionEmail(nextSession.userEmail);
          setMixpanelSessionClinicId(nextSession.clinicId);
        }
        return nextSession;
      });
      return;
    }

    if (nextSessionOrUpdater) {
      setMixpanelSessionEmail(nextSessionOrUpdater.userEmail);
      setMixpanelSessionClinicId(nextSessionOrUpdater.clinicId);
    }
    setSession(nextSessionOrUpdater);
  }, []);

  useEffect(() => {
    setMixpanelSessionEmail(session?.userEmail);
    setMixpanelSessionClinicId(session?.clinicId);
  }, [session?.clinicId, session?.userEmail]);

  const value = useMemo<TSessionContext>(
    () => ({
      session,
      setSession: setSessionWithMixpanel,
      onboardingModal,
      setOnboardingModal,
    }),
    [onboardingModal, session, setSessionWithMixpanel],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
